import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getBatchAttendance } from '../../../services/DashboardOverviewService';
import { useChartTheme } from '../../../utils/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Ordered status, not a categorical set: poor < fair < good. STATUS, so these
// stay literals and never follow the palette -- a user mixing their own
// palette must not be able to make "poor" read as "good". Validated for the
// lightness band, chroma floor, adjacent separation and contrast against the
// chart surface, which is --pg-card: white in every palette, including a
// custom one (utils/palette.js pins the card at L 100, S 0).
//
// The amber/red pair sits in the 6-8 CVD band, which is legal only with a
// second encoding: the band is named in the key above the chart and the
// figures are repeated in the table below. The chrome -- grid, axes, tooltip
// -- follows the palette through the chart theme.
const BAND_COLOR = {
  good: '#12A67B',
  fair: '#C1820F',
  poor: '#D14343',
};

// The reading is a share of seats filled, so a batch is only comparable to
// another once it has run enough sessions to mean something.
const BatchAttendanceReport = () => {
  const theme = useChartTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [end, setEnd] = useState('worst');
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    (async () => {
      try {
        const res = await getBatchAttendance(12);
        setReport(res?.data || null);
      } catch (_) {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const bands = report?.bands || [];
  const counts = report?.counts || {};
  const rows = useMemo(
    () => (end === 'worst' ? report?.worst : report?.best) || [],
    [report, end],
  );

  // Horizontal bars: batch names are long sentences, and rotating them under
  // a vertical axis makes them unreadable.
  const data = useMemo(
    () => ({
      labels: rows.map((b) => b.name),
      datasets: [
        {
          label: 'Attendance',
          data: rows.map((b) => b.rate),
          backgroundColor: rows.map((b) => BAND_COLOR[b.band]),
          hoverBackgroundColor: rows.map((b) => BAND_COLOR[b.band]),
          // Rounded at the value, square at the baseline it grows from.
          borderRadius: 4,
          borderSkipped: 'start',
          barThickness: 14,
        },
      ],
    }),
    [rows],
  );

  const options = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => {
              const b = rows[ctx.dataIndex];
              return [
                ` ${ctx.parsed.x}% of seats filled`,
                ` ${b.sessions} session${b.sessions === 1 ? '' : 's'} · ${b.students} enrolled`,
              ];
            },
          },
        },
      },
      scales: {
        x: theme.valueAxis({
          min: 0,
          max: 100,
          ticks: { stepSize: 25, callback: (v) => `${v}%` },
        }),
        y: theme.categoryAxis({
          ticks: {
            // A batch name can be a full sentence; trim it here rather than
            // letting it eat half the panel.
            callback(value) {
              const label = this.getLabelForValue(value);
              return label.length > 30 ? `${label.slice(0, 30)}…` : label;
            },
          },
        }),
      },
    }),
    [rows, theme],
  );

  return (
    <section className="ba">
      <header className="ba-head">
        <div>
          <h2 className="ba-title">Attendance by batch</h2>
          <p className="ba-sub">
            The share of seats filled across every session a batch has run.
          </p>
        </div>

        {/* Both ends matter: the worst batches are the ones to act on, the
            best are the ones to learn from. */}
        <div className="ba-seg" role="group" aria-label="Which end to show">
          <button
            type="button"
            aria-pressed={end === 'worst'}
            className={`ba-seg-btn${end === 'worst' ? ' is-on' : ''}`}
            onClick={() => setEnd('worst')}
          >
            Needs attention
          </button>
          <button
            type="button"
            aria-pressed={end === 'best'}
            className={`ba-seg-btn${end === 'best' ? ' is-on' : ''}`}
            onClick={() => setEnd('best')}
          >
            Best attended
          </button>
        </div>
      </header>

      {loading ? (
        <div className="ba-state">Working out who turned up…</div>
      ) : failed ? (
        <div className="ba-state">That did not load. Try refreshing.</div>
      ) : !report || report.totalRated === 0 ? (
        <div className="ba-state">
          No batch has attendance recorded against it yet.
        </div>
      ) : (
        <>
          {/* How the whole estate splits, before any one batch is read. */}
          <div className="ba-bands">
            {bands.map((b) => (
              <span className="ba-band" key={b.key}>
                <span
                  className="ba-band-dot"
                  style={{ background: BAND_COLOR[b.key] }}
                  aria-hidden="true"
                />
                <b>{counts[b.key] ?? 0}</b> {b.label}
              </span>
            ))}
            <span className="ba-band ba-band--note">
              {report.totalRated} of {report.totalBatches} batches have
              sessions recorded
            </span>
          </div>

          <div
            className="ba-canvas"
            style={{ height: `${Math.max(180, rows.length * 26 + 30)}px` }}
          >
            <Bar data={data} options={options} />
          </div>

          <details className="ba-table">
            <summary>Show the figures</summary>
            <div className="ba-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Attendance</th>
                    <th>Sessions</th>
                    <th>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id}>
                      <td>{b.name}</td>
                      <td>
                        <span
                          className="ba-rate"
                          style={{ color: BAND_COLOR[b.band] }}
                        >
                          {b.rate}%
                        </span>
                      </td>
                      <td>{b.sessions}</td>
                      <td>{b.students}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
};

export default BatchAttendanceReport;
